import React, { useState, useEffect, useRef } from "react"
import { useTranslations } from "modules/i18n/hooks/useTranslations"
import { retryWithBackoff } from "common/lib/retryWithBackoff"
import { Notification } from "common/components/Notification"
import { Bugsnag } from "common/lib/bugsnag"

// Importing @nprapps/sidechain above creates a custom element, which requires
// us to define a type for it so typescript doesn't complain.
declare module "react" {
  namespace JSX {
    interface IntrinsicElements {
      "side-chain": {
        src: string
        ref?: React.RefObject<HTMLElement | null>
      }
    }
  }
}

export interface IframeProps {
  src: string
  height?: number
  title?: string
}

const lazyloadSidechain = () => retryWithBackoff(() => import("@nprapps/sidechain"))

export const Iframe = ({ src, height, title }: IframeProps) => {
  const [loaded, setLoaded] = useState(false)
  const [loadFailed, setLoadFailed] = useState(false)
  const sidechainRef = useRef<HTMLElement>(null)
  const t = useTranslations("blocks.iframes")

  useEffect(() => {
    let cancelled = false

    lazyloadSidechain()
      .then(() => {
        if (!cancelled) setLoaded(true)
      })
      .catch((error) => {
        Bugsnag.notify(error as Error, (event) => {
          event.severity = "info"
        })
        if (!cancelled) setLoadFailed(true)
      })

    return () => {
      cancelled = true
    }
  }, [])

  // Handles updating the iframes title
  useEffect(() => {
    if (!loaded || !sidechainRef.current) return

    const sidechainEl = sidechainRef.current

    const updateIframeTitle = () => {
      const iframe = sidechainEl.shadowRoot?.querySelector("iframe")
      if (iframe && iframe.getAttribute("title") !== title) {
        iframe.setAttribute("title", title || t("default_accessible_title"))
      }
    }

    // Try straight away
    updateIframeTitle()

    // Setup an observer to watch for the iframe being loaded later, so we can set its title then
    const targetNode = sidechainEl.shadowRoot || sidechainEl

    const observer = new MutationObserver(() => {
      updateIframeTitle()
    })

    observer.observe(targetNode, {
      childList: true, // Watch for added/removed nodes
      subtree: true, // Watch deeply within the tree
    })

    return () => {
      observer.disconnect()
    }
  }, [loaded, title, t])

  // Render an empty placeholder with an appropriate height, if saved
  if (loaded) {
    return <side-chain ref={sidechainRef} src={src}></side-chain>
  } else if (loadFailed) {
    return (
      <div className="mb-4">
        <Notification
          type="info"
          title={t("load_failed_title")}
          message={t("load_failed_message")}
          closable={false}
        />
      </div>
    )
  } else {
    return <div aria-busy="true" style={{ minHeight: height || 0 }}></div>
  }
}
